// Session management configuration
// These values can be overridden by SSM parameters for different environments

export interface SessionConfig {
  sessionTimeoutMinutes: number;
  idleTimeoutMinutes: number;
  warningCountdownSeconds: number;
  enabled: boolean;
}

// Default configuration
const DEFAULT_SESSION_CONFIG: SessionConfig = {
  sessionTimeoutMinutes: 60, // 1 hour total session
  idleTimeoutMinutes: 30, // 30 minutes idle warning
  warningCountdownSeconds: 120, // 2 minutes warning countdown
  enabled: true,
};

// Environment-specific overrides
const ENV_SESSION_CONFIGS: Record<string, Partial<SessionConfig>> = {
  dev: {
    sessionTimeoutMinutes: 60, // 1 hour for dev
    idleTimeoutMinutes: 30, // 30 minutes idle
    warningCountdownSeconds: 120, // 2 minutes warning
    enabled: true,
  },
  stage: {
    sessionTimeoutMinutes: 60, // 1 hour for staging
    idleTimeoutMinutes: 30, // 30 minutes idle
    warningCountdownSeconds: 120, // 2 minutes warning
    enabled: true,
  },
  prod: {
    sessionTimeoutMinutes: 60, // 1 hour for production
    idleTimeoutMinutes: 30, // 30 minutes idle
    warningCountdownSeconds: 120, // 2 minutes warning
    enabled: true,
  },
};

// Load session configuration from API or fallback to environment config
export async function loadSessionConfig(): Promise<SessionConfig> {
  try {
    // Check if we have a config object in window (loaded from API)
    if ((window as any).__SESSION_CONFIG) {
      const apiConfig = (window as any).__SESSION_CONFIG;
      console.log('✅ Loaded session config from API:', apiConfig);
      return { ...DEFAULT_SESSION_CONFIG, ...apiConfig };
    }

    // Try to fetch from API
    try {
      // First try to get session config API URL from config.json
      let sessionConfigApiUrl = (window as any).__SESSION_CONFIG_API_URL;

      if (!sessionConfigApiUrl) {
        // Try to load from config.json
        try {
          const configResponse = await fetch('/config.json', { cache: 'no-cache' });
          if (configResponse.ok) {
            const config = await configResponse.json();
            sessionConfigApiUrl = config.sessionConfigApiUrl;
          }
        } catch (configError) {
          console.warn('⚠️ Failed to load config.json:', configError);
        }
      }

      if (sessionConfigApiUrl && !sessionConfigApiUrl.includes('placeholder.invalid')) {
        const response = await fetch(sessionConfigApiUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          cache: 'no-cache',
        });

        if (response.ok) {
          const apiConfig = await response.json();
          console.log('✅ Loaded session config from API:', apiConfig);
          return { ...DEFAULT_SESSION_CONFIG, ...apiConfig };
        } else {
          console.warn('⚠️ API returned non-200 status:', response.status);
        }
      } else {
        console.warn('⚠️ No session config API URL found');
      }
    } catch (apiError) {
      console.warn('⚠️ Failed to load session config from API:', apiError);
    }

    // Fallback to environment-specific config
    const env = (window as any).__ENV || 'dev';
    const envConfig = ENV_SESSION_CONFIGS[env] || {};
    const config = { ...DEFAULT_SESSION_CONFIG, ...envConfig };

    console.log('✅ Loaded session config for environment:', env, config);
    return config;
  } catch (error) {
    console.warn('⚠️ Failed to load session config, using defaults:', error);
    return DEFAULT_SESSION_CONFIG;
  }
}

// Session configuration context
let sessionConfig: SessionConfig | null = null;

export async function getSessionConfig(): Promise<SessionConfig> {
  if (!sessionConfig) {
    sessionConfig = await loadSessionConfig();
  }
  return sessionConfig;
}

// Reset session config (useful for testing)
export function resetSessionConfig() {
  sessionConfig = null;
}
